import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Scale, Loader2, ArrowLeft, Eye, EyeOff, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../config/api";


export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setIsVerifying(false);
        return;
      }
      
      try {
        await axios.get(`${API}/auth/verify-reset-token/${token}`);
        setTokenValid(true);
      } catch (error) {
        setTokenValid(false);
        toast.error(error.response?.data?.detail || "Token invalid sau expirat");
      } finally {
        setIsVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error("Te rugăm să completezi ambele câmpuri");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Parola trebuie să aibă cel puțin 6 caractere");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Parolele nu coincid");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setResetComplete(true);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "A apărut o eroare");
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center login-bg relative">
        <div className="absolute inset-0 bg-primary/70 backdrop-blur-sm" />
        <Card className="w-full max-w-md mx-4 relative z-10 shadow-2xl border-0">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
            <p className="mt-4 font-body text-muted-foreground">Se verifică link-ul...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center login-bg relative">
        <div className="absolute inset-0 bg-primary/70 backdrop-blur-sm" />
        <Card className="w-full max-w-md mx-4 relative z-10 shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="font-heading text-2xl">Link Invalid</CardTitle>
            <CardDescription className="font-body text-muted-foreground">
              Link-ul de resetare este invalid sau a expirat.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Link to="/forgot-password">
              <Button className="w-full font-body">
                Solicită un nou link
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="w-full font-body">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Înapoi la Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (resetComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center login-bg relative">
        <div className="absolute inset-0 bg-primary/70 backdrop-blur-sm" />
        <Card className="w-full max-w-md mx-4 relative z-10 shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="font-heading text-2xl">Parolă Resetată</CardTitle>
            <CardDescription className="font-body text-muted-foreground">
              Parola ta a fost schimbată cu succes.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/login">
              <Button className="w-full font-body">
                Mergi la Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center login-bg relative">
      <div className="absolute inset-0 bg-primary/70 backdrop-blur-sm" />
      
      <Card className="w-full max-w-md mx-4 relative z-10 shadow-2xl border-0" data-testid="reset-password-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-4">
            <Scale className="w-8 h-8 text-primary-foreground" />
          </div>
          <CardTitle className="font-heading text-2xl">Resetare Parolă</CardTitle>
          <CardDescription className="font-body text-muted-foreground">
            Introdu noua parolă pentru contul tău
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="font-body text-sm font-medium">
                Parolă Nouă
              </Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Introdu parola nouă"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="font-body pr-10"
                  data-testid="new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-body text-sm font-medium">
                Confirmă Parola
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirmă parola nouă"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="font-body pr-10"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <Button
              type="submit"
              className="w-full font-body font-medium"
              disabled={isLoading}
              data-testid="reset-submit-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Se resetează...
                </>
              ) : (
                "Resetează Parola"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
